import React, {useMemo} from 'react'

import Link from 'next/link'
import {Cell, Pie, PieChart} from 'recharts'
import BigNumber from 'bignumber.js'

import useDark from '@hook/useDark'
import useFormatNumber from '@hook/useFormatNumber'
import {useTranslation} from '@lib/intl/useTranslations'
import useQueryUserMini from '@hook/query/leaderboard/useQueryUserMini'

import {getLinkUrl, URL_PORTFOLIO} from '@constant/url'

import Text from '@component/Text/Text'
import WizButton from '@component/buttons/WizButton'
import Spinner from '@component/Spinner/Spinner'

import IconMDD from '@svg/leaderboard/ic_mdd_colorful.svg'
import IconHoldAvg from '@svg/leaderboard/ic_hold_avg_colorful.svg'
import IconTf from '@svg/leaderboard/ic_trade_frequency_colorful.svg'
import IconBalance from '@svg/leaderboard/ic_portfolio_balance_colorful.svg'
import IconMDDDark from '@svg/leaderboard/ic_mdd_colorful_dark.svg'
import IconHoldAvgDark from '@svg/leaderboard/ic_hold_avg_colorful_dark.svg'
import IconTfDark from '@svg/leaderboard/ic_trade_frequency_colorful_dark.svg'
import IconBalanceDark from '@svg/leaderboard/ic_portfolio_balance_colorful_dark.svg'
import {UserPrivacy} from '@type/profile'
import {useLoginStore} from '@store/LoginStore'

interface IProps {
    className?: string
    id: string
    isExpanded: boolean
}

const LeagueExpandedUserCell: React.FC<IProps> = ({className, id, isExpanded}) => {
    const t = useTranslation()
    const {isRenderDark} = useDark()
    const loginId = useLoginStore(state => state.id)
    const {displayBalance, displayApproximateBalance} = useFormatNumber()

    const {data: miniData, isLoading: isLoadingMiniData} = useQueryUserMini(id, isExpanded)

    const winPercentage: number = useMemo(() => {
        if (!miniData) return 0.0
        const sum = miniData.data?.win + miniData.data?.loss
        if (sum === 0) return 0.0
        return (miniData.data?.win * 100) / sum
    }, [miniData])

    const winPerformanceChartData = useMemo(() => {
        if (!miniData)
            return [
                {name: 'win', value: 0},
                {name: 'loss', value: 100},
            ]
        return [
            {name: 'win', value: winPercentage},
            {name: 'loss', value: 100 - winPercentage},
        ]
    }, [winPercentage, miniData])
    const holdAvg = useMemo(() => new BigNumber(miniData?.data.hold_avg || 0.0).div(86400).toFormat(2), [miniData])

    const isPortfolioPrivate = useMemo(() => {
        if (id === loginId) {
            return false
        }
        if (
            miniData?.data?.privacy === UserPrivacy.Private ||
            miniData?.data?.privacy === UserPrivacy.OpenToSubscriber ||
            miniData?.data?.privacy === UserPrivacy.CompletelyPrivate
        ) {
            return true
        } else return false
    }, [miniData?.data?.privacy])

    return (
        <div className={'min-h-[102px] mt-[20px]'}>
            {isLoadingMiniData && (
                <div className={'bg-white dark:bg-bg_dark_white02 rounded-lg flex py-8 justify-center'}>
                    <Spinner size={'small'} visible={true} />
                </div>
            )}
            {!isLoadingMiniData && (
                <div
                    className={
                        'bg-white dark:bg-bg_dark_white02 rounded-[5px] flex justify-between items-center gap-[30px] px-[20px] py-[30px]'
                    }>
                    <div className={'flex gap-[10px]'}>
                        {winPerformanceChartData && (
                            <PieChart width={37} height={37}>
                                <Pie
                                    data={winPerformanceChartData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={18}
                                    innerRadius={9}
                                    cornerRadius={4}
                                    stroke={'transparent'}
                                    label={false}>
                                    <Cell
                                        key={`slice-0`}
                                        fill="#4FAC85"
                                        strokeLinecap={'round'}
                                        strokeLinejoin={'round'}
                                    />
                                    <Cell key={`slice-1`} fill="#C53747" />
                                </Pie>
                            </PieChart>
                        )}

                        <div>
                            {/*Performance*/}
                            <Text className={'text-ti3 text-gray03 dark:text-dark_gray03'}>
                                {t('leaderboard.card.title.performance')}
                            </Text>
                            <Text className={'text-primary_shade dark:text-dark_primary_shade'}>
                                {t('leaderboard.card.value.win')} {winPercentage.toFixed(0)}%
                            </Text>
                        </div>
                    </div>

                    <div className={'flex gap-[10px] '}>
                        {isRenderDark ? <IconBalanceDark /> : <IconBalance />}
                        <div>
                            {/*Portfolio Balance*/}
                            <Text className={'text-ti3 text-gray03 dark:text-dark_gray03'}>
                                {t('leaderboard.card.title.portfolioBalance')}
                            </Text>
                            <Text className={'text-gray01 dark:text-dark_gray01'}>
                                {isPortfolioPrivate
                                    ? displayApproximateBalance(miniData?.data?.balance_app || 0, {
                                          showPreSign: true,
                                      })
                                    : displayBalance(miniData?.data?.balance_app || 0, {
                                          showPreSign: true,
                                      })}
                            </Text>
                        </div>
                    </div>

                    <div className={'flex gap-[10px]'}>
                        {isRenderDark ? <IconMDDDark /> : <IconMDD />}
                        <div>
                            {/*MDD*/}
                            <Text className={'text-ti3 text-gray03 dark:text-dark_gray03'}>
                                {t('leaderboard.card.title.mdd')}
                            </Text>
                            <Text className={'text-red_shade dark:text-dark_red_shade'}>
                                {miniData?.data?.mdd?.toFixed(0)}%
                            </Text>
                        </div>
                    </div>

                    <div className={'flex gap-[10px]'}>
                        {isRenderDark ? <IconHoldAvgDark /> : <IconHoldAvg />}
                        <div>
                            {/*Hold Average*/}
                            <Text className={'text-ti3 text-gray03 dark:text-dark_gray03'}>
                                {t('leaderboard.card.title.holdAverage')}
                            </Text>
                            <Text className={'text-gray01 dark:text-dark_gray01'}>
                                {holdAvg} {t('leaderboard.card.value.holdAvgDays')}
                            </Text>
                        </div>
                    </div>

                    <div className={'flex gap-[10px]'}>
                        {isRenderDark ? <IconTfDark /> : <IconTf />}
                        <div>
                            {/*Trade frequency*/}
                            <Text className={'text-ti3 text-gray03 dark:text-dark_gray03'}>
                                {t('leaderboard.card.title.tradeFrequecy')}
                            </Text>
                            <Text className={'text-gray01 dark:text-dark_gray01'}>
                                {new BigNumber(miniData?.data?.tf || 0).toFormat(0)}x
                            </Text>
                        </div>
                    </div>

                    <div className={'flex justify-center items-center'}>
                        <Link
                            onClick={e => {
                                e.stopPropagation()
                            }}
                            href={getLinkUrl(URL_PORTFOLIO(id))}>
                            <WizButton
                                buttonType={'border'}
                                showArrow={true}
                                size={'small'}
                                dataCy={'btn_leaderboard_main_profile'}
                                text={t('leaderboard.card.button.openProfile')}
                                isLink={true}
                            />
                        </Link>
                    </div>
                </div>
            )}
        </div>
    )
}
export default React.memo(LeagueExpandedUserCell)
